import { Icon } from "@iconify/react"
import { Button, Card, Flex, Grid, Input, message, Pagination, Table } from "antd"
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import useTitleStore from "../../store/titleStore";
import useDebounce from "../../hooks/useDebounce";
import { disturbanceInformationService } from "../../services/disturbance-information.service";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTableHeight } from "../../hooks/useTableHeight";
import { format } from 'date-fns';
import TableAction from "../../components/TableAction";
import UpdatedByCell from "../../components/UpdatedByCell";

export default function DisturbanceInformationIndex(){

  const breakpoint = Grid.useBreakpoint()

  const navigate = useNavigate();
  const { setTitle } = useTitleStore();
  const queryClient = useQueryClient();
  const tableHeight = useTableHeight();
  
  const [messageApi, contextHolder] = message.useMessage();
  const [keyword, setKeyword] = useState("");
  const debouncedKeyword = useDebounce(keyword, 500);
  const [dataParams, setDataParams] = useState({
    limit: 10,
    page: 1,
    filter: []
  })

  const { data: informationData , isLoading, isRefetching } = useQuery({
    queryKey: ['disturbanceInformations', dataParams],
    queryFn: () => disturbanceInformationService.fetchAll(dataParams),
    placeholderData: keepPreviousData,
  })

  const { mutate: handleDelete, isPending: deleteLoading } = useMutation({
    mutationFn: (id) => disturbanceInformationService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries('disturbanceInformations');
      messageApi.success('Data berhasil dihapus');
    },
    onError: (error) => {
      console.log(error)
    }
  })

  useEffect(() => {
    setTitle("Informasi Gangguan");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setDataParams((prev) => ({
      ...prev,
      filter: [
        {
          field: 'title',
          operator: 'like',
          value: debouncedKeyword
        }
      ]
    }));
  }, [debouncedKeyword]);

  const handleChangePage = (page, pageSize) => {
    setDataParams({
      ...dataParams,
      limit: pageSize,
      page
    })
  }

  const columns = [
    {
      title: 'No',
      align: 'center',
      width: 80,
      render: (text, record, index) =>  index + 1,
      fixed: breakpoint.md && 'left'
    },
    {
      title: 'Judul',
      dataIndex: 'title',
      width: 210,
      fixed: breakpoint.md && 'left'
    },
    {
      title: 'Lokasi',
      dataIndex: 'location',
      width: 210
    },
    {
      title: 'Tanggal Gangguan',
      dataIndex: 'disturbance_date',
      width: 210,
      render: val => format(new Date(val), 'EEEE, dd-MM-yyyy')
    },
    {
      title: 'Waktu Mulai',
      width: 210,
      render: val => {
        const [hours, minutes] = val.start_time.split(":").map(Number);
        const startDateTime = new Date(val.start_date);
        startDateTime.setHours(hours, minutes, 0);
        return format(startDateTime, 'EEEE, dd-MM-yyyy hh:mm')
      }
    },
    {
      title: 'Waktu Berakhir',
      width: 210,
      render: val => {
        const [hours, minutes] = val.end_time.split(":").map(Number);
        const endDateTime = new Date(val.end_date);
        endDateTime.setHours(hours, minutes, 0);
        return format(endDateTime, 'EEEE, dd-MM-yyyy hh:mm')
      }
    },
    {
      title: 'Waktu Pemulihan',
      dataIndex: 'recovery_time',
      width: 210,
    },
    {
      title: 'Tanggal Update',
      width: 210,
      render: val => <UpdatedByCell value={val} />,
    },
    {
      className: 'last-cell-p',
      title: 'Aksi',
      align: 'right',
      width: 120,
      fixed: 'right',
      render: record => (
        <TableAction
          onClickEdit={() => navigate('/disturbance-information/detail/'+record.id)}
          handleDelete={() => handleDelete(record.id)}
          deleteLoading={deleteLoading}
        />
      )
    },
  ]



  return (
    <Card
      styles={{
        body: {
          paddingLeft: 0,
          paddingRight: 0
        }
      }}
    >
      {contextHolder}
      <Flex gap={20} justify="space-between" style={{ paddingInline: 24, marginBottom: 24 }} >
        <Input 
          style={{ width: 320 }} 
          prefix={<Icon icon="hugeicons:search-01" width="16" height="16" />}
          placeholder="cari..."
          onChange={e => setKeyword(e.target.value)}
          value={keyword}
        />
        <Button 
          variant="solid" 
          color="primary"  
          icon={<Icon icon="duo-icons:add-circle" width="22" height="22" />}
          onClick={() => navigate('/disturbance-information/create')}
          style={{ minWidth: 38 }}
        >
          {breakpoint.md && 'Tambah Informasi'}
        </Button>
      </Flex>
      <Table
        columns={columns}
        pagination={false}
        size="small"
        loading={isLoading || isRefetching}
        rowKey={'id'}
        scroll={{ y: tableHeight }}
        dataSource={informationData?.data}
      />
       <Pagination
          style={{ marginInline: 24, marginTop: 16 }}
          total={informationData?.pagination?.total}
          showSizeChanger={{
            showSearch: false,
          }}
          pageSizeOptions={['10', '20', '50']}
          current={informationData?.pagination?.page}
          pageSize={dataParams.limit}
          align="end"
          onChange={handleChangePage}
        />
    </Card>
  )
}